import Link from 'next/link';
import { Settings } from '../data/api';
import styles from '../styles/Header.module.scss';
import Icon from './Icon';
import DownloadDataModal from './map/DownloadDataModal';
import LinearUnitSelector from './LinearUnitSelector';
import { Select } from './Select';
import { useContext } from 'react';
import { useRouter } from 'next/router';
import { HistoryContext } from '../utils/use-history';
import { set } from 'core-js/core/dict';

interface HeaderProps {
    settings: Settings;
    map: any;
}

function getSelectedRegion(history: string[], settings: Settings): string {
    // Get the slug of the last region page the user has visited, with all query params
    // stripped out. This will return `undefined` if the user hasn't visited any regions.
    const regionPaths = history.filter(path => path.includes('regions'));
    const lastVisitedRegion = regionPaths
        .slice(-1)[0]
        ?.split('/')
        .slice(-1)[0]
        .split('?')[0];
    // If we navigated to this page from a region page that is associated with this dashboard,
    // the region selection dropdown's default should be the region we navigated from.
    // Otherwise, it should be the first item in the list of associated regions.
    return lastVisitedRegion &&
        settings.region_dashboards
            .map(regionDashboard => regionDashboard.slug)
            .includes(lastVisitedRegion)
        ? lastVisitedRegion
        : settings.region_dashboards[0].slug;
}

function Header({ settings, map }: HeaderProps) {
    const router = useRouter();
    const { history } = useContext(HistoryContext);

    let regionDashboardItems = {};
    settings.region_dashboards.forEach(regionDashboard => {
        regionDashboardItems[regionDashboard.slug] = {
            key: regionDashboard.slug,
            name: regionDashboard.name,
        };
    });

    return (
        <header className={styles.header}>
            <div className={styles.items}>
                <Link href="/">
                    <a className={styles.link} rel="noopener">
                        <img
                            alt="Ride Report"
                            src="/logo-on-light.svg"
                            className={styles.logo}
                        />
                        <span className={styles.bar} />
                    </a>
                </Link>
                <div className={styles.breadcrumbsContainer}>
                    <Link href="/">
                        <a className={styles.link} rel="noopener">
                            Micromobility Dashboard
                            <Icon
                                icon="Breadcrumb"
                                className={styles.breadcrumb}
                            />
                        </a>
                    </Link>
                    {settings.region_dashboards.length === 1 ? (
                        <Link
                            href={`/regions/${settings.region_dashboards[0].slug}`}
                        >
                            <a className={styles.link} rel="noopener">
                                {settings.region_dashboards[0].name}
                                <Icon
                                    icon="Breadcrumb"
                                    className={styles.breadcrumb}
                                />
                            </a>
                        </Link>
                    ) : settings.region_dashboards.length > 1 ? (
                        <div className={styles.locationName}>
                            <Select
                                dropdownSide="left"
                                items={regionDashboardItems}
                                onChange={newRegion =>
                                    router.push(`/regions/${newRegion}`)
                                }
                                selectedItemKey={getSelectedRegion(
                                    history,
                                    settings
                                )}
                            />
                            <Icon
                                icon="Breadcrumb"
                                className={styles.breadcrumb}
                            />
                        </div>
                    ) : null}
                    <span className={styles.locationName}>
                        {settings.region_location_name}
                    </span>
                </div>
                <div className={styles.rightSideControls}>
                    <div className={styles.linearUnitSelector}>
                        <LinearUnitSelector></LinearUnitSelector>
                    </div>
                    <div className={styles.downloadData}>
                        <DownloadDataModal
                            map={map}
                            earliestAvailableMetrics={
                                settings.earliest_available_metrics
                            }
                            applyExternalTripCounts={
                                settings.apply_external_trip_counts
                            }
                        />
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Header;
